import { Icon } from "@studio3t/generic-components";
import { useState } from "react";
import { useTestGenerate } from "@/hooks/useApi";
import type { TestSuiteCase } from "@/types/api";
import { testRowsFromSuiteCase, usePipelineStaging } from "./pipelineStaging";
import { pipelineScripts } from "./useScriptRun";
import { useEnsureStagedSuite, useSuiteCatalog } from "./useTestSuiteSync";

// The suite row's wand: the old /studio's AI generation (POST
// /transform/test/generate) rebased onto the suite-group model. The
// staged transforms' scripts (or the untitled drafts) and the input
// document go out; the returned cases are written into the suite in one
// catalog write and appended to the group as saved rows.

/** A name free among the staged rows and the batch — "name", "name 2",
 *  "name 3", … — so a generated batch never overwrites an existing case. */
function freeName(name: string, taken: Set<string>): string {
  let candidate = name;
  for (let i = 2; taken.has(candidate); i++) candidate = `${name} ${i}`;
  taken.add(candidate);
  return candidate;
}

/** The generator wants the parsed input document; free-form text that
 *  isn't JSON generates against an empty document (old /studio rule). */
export function parseInputJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
}

// Generation is available at ANY time: with no scripts staged (and no
// untitled drafts) the batch generates against the identity pipeline —
// the backend rejects an empty scripts array, and a passthrough baseline
// is still a useful starting test.
export const PASSTHROUGH_SCRIPT = `fn transform(data, attributes) {
  result(data, attributes)
}`;

// The response's cases as suite cases, named clear of the staged rows.
function generatedCases(
  generated: Array<{ name: string; assertions?: TestSuiteCase["assertions"] }>,
  tests: ReadonlyArray<{ name: string }>,
): TestSuiteCase[] {
  const taken = new Set(tests.map((t) => t.name));
  return generated.map((c) => ({
    name: freeName(c.name, taken),
    assertions: c.assertions ?? [],
    strict_mode: false,
  }));
}

export function useGenerateSuiteTests() {
  const {
    stagedSuite,
    tests,
    transforms,
    scriptDrafts,
    savedScripts,
    inputDocument,
    appendSavedTests,
    openTestViewer,
  } = usePipelineStaging();
  const { saveCases } = useSuiteCatalog();
  const ensureSuite = useEnsureStagedSuite();
  const { mutateAsync: generateTests } = useTestGenerate();
  const [generating, setGenerating] = useState(false);

  // Generate, store and stage a batch; the first new case's id, or null
  // when the engine returned none.
  const generateBatch = async (): Promise<string | null> => {
    const resolved = await pipelineScripts(
      transforms,
      scriptDrafts,
      savedScripts,
    );
    const response = await generateTests({
      scripts: resolved.length > 0 ? resolved : [PASSTHROUGH_SCRIPT],
      sample_input: parseInputJson(inputDocument),
    });
    const cases = generatedCases(response?.item?.cases ?? [], tests);
    if (cases.length === 0) return null;
    // A PENDING group's first batch creates the stored suite here —
    // the suite only reaches the tree holding its generated tests.
    await saveCases(await ensureSuite(), cases);
    const ids = appendSavedTests(
      cases.map((c) => ({
        name: c.name,
        rows: testRowsFromSuiteCase(c),
        strictMode: c.strict_mode ?? false,
      })),
      Icon.Test,
    );
    return ids[0] ?? null;
  };

  const generate = async () => {
    if (generating || !stagedSuite) return;
    setGenerating(true);
    try {
      // Show what was generated right away: the first new case opens in
      // the viewer, as a click on its row would.
      const first = await generateBatch();
      if (first) openTestViewer(first);
    } catch (error) {
      // A failed generation leaves the panel and catalog untouched.
      console.error("Failed to generate tests:", error);
    } finally {
      setGenerating(false);
    }
  };

  return { generate, generating };
}
